import { Download, FileText, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FACTURE_STATUTS,
  FACTURE_STATUT_LABELS,
  type Facture,
  type FactureStatut,
} from '@sirajoptic/shared';
import { BadgeStatutFacture } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/field';
import { ChargementBloc, EtatErreur, EtatVide } from '@/components/ui/feedback';
import { Pagination } from '@/components/ui/pagination';
import { TBody, TD, TH, THead, TR, Table } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { PageBody, PageHeader } from '@/layouts/AppShell';
import { date, dh, tronquer } from '@/lib/format';
import { useFactures } from './api/factures.api';
import { telechargerFacture } from './pdf/facture-pdf';

export function FacturesPage() {
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState<FactureStatut | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const naviguer = useNavigate();
  const toast = useToast();

  const { data, isPending, error, refetch, isFetching } = useFactures({
    page,
    limit: 25,
    q: recherche || undefined,
    statut: statut || undefined,
    from: from || undefined,
    to: to || undefined,
  });

  async function telecharger(facture: Facture) {
    try {
      await telechargerFacture(facture);
    } catch {
      toast.erreur("Le PDF n'a pas pu être généré.");
    }
  }

  return (
    <>
      <PageHeader
        titre="Factures"
        description="Documents officiels — une facture émise ne se modifie plus."
        actions={
          <Button
            variant="primary"
            icone={<Plus aria-hidden className="h-4 w-4" />}
            onClick={() => naviguer('/factures/nouvelle')}
          >
            Nouvelle facture
          </Button>
        }
      />

      <PageBody>
        <Card>
          <div className="flex flex-wrap items-center gap-2 px-4 py-3">
            <div className="relative min-w-0 flex-1 sm:max-w-sm">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
              />
              <Input
                value={recherche}
                onChange={(e) => {
                  setRecherche(e.target.value);
                  setPage(1);
                }}
                placeholder="Référence ou client…"
                className="pl-8"
                aria-label="Rechercher une facture"
              />
            </div>

            <Select
              value={statut}
              onChange={(e) => {
                setStatut(e.target.value as FactureStatut | '');
                setPage(1);
              }}
              className="w-auto"
              aria-label="Filtrer par statut"
            >
              <option value="">Tous les statuts</option>
              {FACTURE_STATUTS.map((s) => (
                <option key={s} value={s}>
                  {FACTURE_STATUT_LABELS[s]}
                </option>
              ))}
            </Select>

            <label className="flex items-center gap-2 text-sm text-ink-muted">
              Du
              <Input
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setPage(1);
                }}
                className="w-auto"
              />
              au
              <Input
                type="date"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setPage(1);
                }}
                className="w-auto"
              />
            </label>

            {isFetching ? <span className="text-xs text-ink-muted">Actualisation…</span> : null}
          </div>

          {isPending ? (
            <ChargementBloc />
          ) : error ? (
            <EtatErreur erreur={error} onReessayer={() => void refetch()} />
          ) : data.data.length === 0 ? (
            <EtatVide
              titre="Aucune facture"
              description="Créez une facture, ou générez-en une depuis une commande."
              icone={<FileText aria-hidden className="h-6 w-6" />}
              action={
                <Button variant="primary" onClick={() => naviguer('/factures/nouvelle')}>
                  Nouvelle facture
                </Button>
              }
            />
          ) : (
            <>
              <Table>
                <THead>
                  <TR>
                    <TH>N°</TH>
                    <TH>Référence</TH>
                    <TH>Date</TH>
                    <TH>Client</TH>
                    <TH numerique>Total HT</TH>
                    <TH numerique>TVA</TH>
                    <TH numerique>Total TTC</TH>
                    <TH>Statut</TH>
                    <TH className="text-right">PDF</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.data.map((f) => (
                    <TR key={f.id} onClick={() => naviguer(`/factures/${f.id}`)}>
                      <TD className="text-ink-muted tabular">{f.numero_ordre}</TD>
                      <TD className="whitespace-nowrap font-mono font-medium">{f.reference}</TD>
                      <TD className="whitespace-nowrap text-ink-muted">{date(f.date_facture)}</TD>
                      <TD>{tronquer(f.client_nom, 28)}</TD>
                      <TD numerique>{dh(f.total_ht)}</TD>
                      <TD numerique className="text-ink-muted">
                        {dh(f.total_tva)}
                      </TD>
                      <TD numerique className="font-semibold">
                        {dh(f.total_ttc)}
                      </TD>
                      <TD>
                        <BadgeStatutFacture statut={f.statut} />
                      </TD>
                      <TD className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Télécharger ${f.reference}`}
                          title="Télécharger le PDF"
                          onClick={(e) => {
                            e.stopPropagation();
                            void telecharger(f);
                          }}
                        >
                          <Download aria-hidden className="h-4 w-4" />
                        </Button>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
              <Pagination meta={data.meta} onPage={setPage} libelle="factures" />
            </>
          )}
        </Card>
      </PageBody>
    </>
  );
}
